import { Button, Stack, Typography, useTheme } from "@mui/material";
import { type FC, useEffect } from "react";
import { toast } from "sonner";
import type { ClientPortfolioManualValueSaveRequestDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InvestmentValueFormFields } from "web_myplann/Portfolio/Components/Redesign/Detail/Value/InvestmentValueFormFields";
import { getInvestmentValueFormDefaults } from "web_myplann/Portfolio/Components/Redesign/Detail/Value/investmentValueFormDefaults";
import { useInvestmentValueForm } from "web_myplann/Portfolio/Components/Redesign/Detail/Value/useInvestmentValueForm";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";

type Props = {
	open: boolean;
	onClose: () => void;
	subtitle: string;
	defaultInvested: number | null;
	previousValue: number | null;
	previousValueDate: string | null;
	currencySign: string;
	onBack?: () => void;
	isPending: boolean;
	onSave: (
		request: ClientPortfolioManualValueSaveRequestDTO,
		onSuccess: () => void,
	) => void;
};

const PageResources = Resources.PortfolioDetail.InvestmentValue;

export const InvestmentValueDrawer: FC<Props> = ({
	open,
	onClose,
	subtitle,
	defaultInvested,
	previousValue,
	previousValueDate,
	currencySign,
	onBack,
	isPending,
	onSave,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const form = useInvestmentValueForm({ defaultInvested });

	useEffect(() => {
		if (open) {
			form.reset(getInvestmentValueFormDefaults(defaultInvested));
		}
	}, [open, defaultInvested, form.reset]);

	const onSubmit = form.handleSubmit((values) => {
		onSave(
			{
				value: values.value,
				invested: values.invested,
				date: values.date,
			},
			() => {
				toast.success(t(PageResources.savedToast));
				onClose();
			},
		);
	});

	return (
		<MyPlannDrawer
			grabber
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(PageResources.updateTitle)}
		>
			<Stack component="form" onSubmit={onSubmit} pb={theme.spacingTokens.lg}>
				<Typography
					variant="body2"
					px={theme.spacingTokens.lg}
					pb={theme.spacingTokens.sm}
				>
					{subtitle}
				</Typography>
				{previousValue !== null && previousValueDate !== null && (
					<Typography
						variant="body2"
						color={theme.palette.grey[500]}
						px={theme.spacingTokens.lg}
						pb={theme.spacingTokens.md}
						tabular
					>
						{t(PageResources.previousValue)}{" "}
						<Typography
							component="span"
							variant="bodyMedium"
							color={theme.palette.grey[600]}
						>
							{moneyFormatter(previousValue, currencySign, "", 0)}
						</Typography>{" "}
						<Typography component="span" color={theme.palette.grey[300]}>
							({dateFormatter(previousValueDate)})
						</Typography>
					</Typography>
				)}
				<InvestmentValueFormFields
					control={form.control}
					currencySign={currencySign}
				/>
				<Stack
					px={theme.spacingTokens.lg}
					pt={theme.spacingTokens.lg}
					gap={theme.spacingTokens.sm}
				>
					<Button type="submit" variant="primary" loading={isPending}>
						{t(PageResources.confirm)}
					</Button>
					{onBack && (
						<Stack alignItems="center" py={theme.spacingTokens.sm}>
							<Button variant="textLink" onClick={onBack}>
								{t(PageResources.backToFund)}
							</Button>
						</Stack>
					)}
				</Stack>
			</Stack>
		</MyPlannDrawer>
	);
};
